import { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from './components/LoadingSpinner';
import AccessibilityEnhancer from './components/AccessibilityEnhancer';
import SecurityEnhancer from './components/SecurityEnhancer';
import MobileOptimizer from './components/MobileOptimizer';
import { usePerformance } from './hooks/usePerformance';
// AdSense disabled
import Header from './components/Header';
import ProgressSteps from './components/ProgressSteps';
import PremiumSidebar from './components/PremiumSidebar';
import PremiumNavbar from './components/PremiumNavbar';
import PremiumContainer from './components/PremiumContainer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CookieProvider } from './contexts/CookieContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { initConsoleFilter } from './utils/consoleFilter';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SuperUserRoute } from './components/SuperUserRoute';
import { PremiumRoute } from './components/PremiumRoute';
import CookieConsent from './components/CookieConsent';
import AdBlockNotice from './components/AdBlockNotice';
import AdSenseLoader from './components/ads/AdSenseLoader';
import ErrorBoundary from './components/ErrorBoundary';
import './utils/adsenseDebug'; // Load debug utility
import './utils/adsenseDiagnostic'; // Load diagnostic utility
import './utils/adsenseConfigCheck'; // Load config check utility
import './utils/checkAdSenseScript'; // Load script check utility
import './utils/forceAdInit'; // Load force ad init utility
import { listCVs } from './services/api';
import { initPerformanceOptimizations } from './utils/performanceOptimizer';

// Lazy load routes for better code splitting
const LandingPage = lazy(() => import('./components/LandingPage'));
const Auth = lazy(() => import('./pages/Auth'));
const PreAuthCVUpload = lazy(() => import('./components/PreAuthCVUpload'));
const CVUpload = lazy(() => import('./components/CVUpload'));
const CompaniesMatching = lazy(() => import('./components/CompaniesMatching'));
const InterviewPrepNew = lazy(() => import('./components/InterviewPrepNew'));
const InterviewPrepList = lazy(() => import('./components/InterviewPrepList'));
const InterviewPrepView = lazy(() => import('./components/InterviewPrepView'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const MobileDashboard = lazy(() => import('./components/MobileDashboard'));
const MobileLayout = lazy(() => import('./components/MobileLayout'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const OpenAIJobSearch = lazy(() => import('./pages/OpenAIJobSearch'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const OurMission = lazy(() => import('./pages/OurMission'));
const Team = lazy(() => import('./pages/Team'));
const Products = lazy(() => import('./pages/Products'));
const CVScanner = lazy(() => import('./pages/CVScanner'));
const ProfileBoost = lazy(() => import('./pages/ProfileBoost'));
const ProfileBoostFunctional = lazy(() => import('./components/ProfileBoost'));
const CompanyMatch = lazy(() => import('./pages/CompanyMatch'));
const InterviewPrep = lazy(() => import('./pages/InterviewPrep'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const GDPR = lazy(() => import('./pages/GDPR'));
const ATSGuide = lazy(() => import('./pages/ATSGuide'));
const Blog = lazy(() => import('./pages/Blog'));
const Article = lazy(() => import('./pages/Article'));
const ArticleKPIs = lazy(() => import('./pages/ArticleKPIs'));
const Resources = lazy(() => import('./pages/Resources'));
const CareerAcceleration = lazy(() => import('./pages/CareerAcceleration'));
const JobsInCountry = lazy(() => import('./pages/JobsInCountry'));
const JobSearch = lazy(() => import('./pages/JobSearch'));
const CompaniesInCountry = lazy(() => import('./pages/CompaniesInCountry'));
const CVTips = lazy(() => import('./pages/CVTips'));
const InteractiveGuide = lazy(() => import('./components/InteractiveGuide'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const Pricing = lazy(() => import('./pages/Pricing'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Scroll to top component - optimized with requestAnimationFrame
// Language handler component - changes i18n language when URL parameter changes
function LanguageHandler() {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('lang');
    if (langParam && ['en', 'fr', 'es'].includes(langParam) && i18n.language !== langParam) {
      i18n.changeLanguage(langParam);
    }
  }, [location.search, i18n]);
  
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [pathname]);

  return null;
}

// Main App Layout Component
function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const pathname = location.pathname;
  const [showInteractiveGuide, setShowInteractiveGuide] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine current step from URL
  const getCurrentStep = () => {
    if (pathname.includes('/cv-upload')) return 1;
    if (pathname.includes('/profile-boost')) return 2;
    if (pathname.includes('/company-matching')) return 3;
    if (pathname.includes('/interview-prep')) return 4;
    if (pathname.includes('/dashboard')) return 5;
    return 1;
  };

  const currentStep = getCurrentStep();

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  const goToStep = (step: number) => {
    const routes = [
      '/cv-upload',
      '/profile-boost-functional',
      '/company-matching', 
      '/interview-prep-functional',
      '/dashboard'
    ];
    navigate(routes[step - 1]);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // Mobile sidebar toggle state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <>
      <style>{`
        .appLayout {
          display: flex;
          flex-direction: column;
          min-height: 100dvh;
          width: 100%;
          background-color: #f3f4f6;
        }

        .appLayout-header {
          position: sticky;
          top: 16px;
          z-index: 100;
          background: transparent;
          height: 72px;
          flex-shrink: 0;
          margin-left: 276px; /* 260px sidebar + 16px gap */
          margin-right: 16px;
          margin-top: 16px;
          margin-bottom: 0;
          width: calc(100% - 292px); /* Full width minus sidebar and margins */
        }

        .appLayout-body {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* Desktop Sidebar - Floating Rounded Bar */
        .appLayout-sidebar {
          width: 260px;
          flex: 0 0 260px;
          height: calc(100dvh - 104px);
          overflow-y: auto;
          overflow-x: hidden;
          position: sticky;
          top: 104px;
          margin-left: 16px;
          margin-top: 16px;
          margin-bottom: 16px;
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(229, 231, 235, 0.5);
        }

        /* Main Content */
        .appLayout-content {
          flex: 1;
          min-width: 0;
          overflow-y: auto;
          overflow-x: hidden;
          background-color: #f3f4f6;
          margin-left: 16px;
          margin-right: 16px;
        }

        .appLayout-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }

        /* Mobile */
        @media (max-width: 1023px) {
          .appLayout-header {
            top: 8px;
            margin: 8px;
            margin-bottom: 0;
            margin-left: 8px;
            height: 72px;
          }

          .appLayout-sidebar {
            position: fixed;
            top: 80px;
            left: 0;
            height: calc(100dvh - 80px);
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            z-index: 90;
            margin: 0;
            border-radius: 0 20px 20px 0;
            box-shadow: 2px 0 10px rgba(0,0,0,0.15);
            border: none;
            border-right: 1px solid rgba(229, 231, 235, 0.5);
          }

          .appLayout-sidebar.mobile-open {
            transform: translateX(0);
          }

          .appLayout-content {
            margin-left: 0;
            margin-right: 0;
          }
        }

        /* Sidebar Scrollbar */
        .appLayout-sidebar::-webkit-scrollbar {
          width: 6px;
        }
        .appLayout-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }
        .appLayout-sidebar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .appLayout-sidebar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        /* Content Scrollbar */
        .appLayout-content::-webkit-scrollbar {
          width: 8px;
        }
        .appLayout-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .appLayout-content::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .appLayout-content::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      {/* Interactive Guide */}
      {showInteractiveGuide && (
        <InteractiveGuide
          onClose={() => setShowInteractiveGuide(false)}
          onComplete={() => {
            setShowInteractiveGuide(false);
            navigate('/cv-upload');
          }}
        />
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-[80] transition-opacity duration-300"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div className="appLayout">
        {/* Header - Sticky Top Navbar */}
        <header className="appLayout-header">
          <PremiumNavbar 
            onSignOut={handleSignOut}
            onMenuToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            isMobileMenuOpen={isMobileSidebarOpen}
          />
        </header>

        {/* Body Container */}
        <div className="appLayout-body">
          {/* Sidebar - Fixed Left Navigation */}
          <aside className={`appLayout-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
            <PremiumSidebar
              currentStep={currentStep} 
              onStepClick={(step) => {
                goToStep(step);
                setIsMobileSidebarOpen(false);
              }}
              onDashboardClick={() => {
                goToDashboard();
                setIsMobileSidebarOpen(false);
              }}
              showDashboard={true}
            />
          </aside>

          {/* Main Content Area - Centered Container */}
          <main className="appLayout-content">
            <div className="appLayout-container">
              <PremiumContainer>
                {children}
              </PremiumContainer>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

// Landing Page with Navigation
function LandingPageWithNav() {
  const navigate = useNavigate();
  return <LandingPage onGetStarted={() => navigate('/upload-cv')} />;
}

// Auth Page with Navigation
function AuthWithNav() {
  const navigate = useNavigate();
  return (
    <Auth 
      onBack={() => navigate('/')}
      onLogin={() => navigate('/cv-upload')}
      onSignUp={() => navigate('/cv-upload')}
      onForgotPassword={() => {}}
    />
  );
}

// CV Upload with AppLayout
function CVUploadWithLayout() {
  const navigate = useNavigate();
  return (
    <AppLayout>
      <CVUpload 
        onComplete={() => navigate('/company-matching')} 
      />
    </AppLayout>
  );
}

// Profile Boost Functional with AppLayout
function ProfileBoostFunctionalWithLayout() {
  const navigate = useNavigate();
  return (
    <AppLayout>
      <ProfileBoostFunctional />
    </AppLayout>
  );
}

// Job Matching with AppLayout
function CompaniesMatchingWithLayout() {
  const navigate = useNavigate();
  const [cvAnalysisId, setCvAnalysisId] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [allCVs, setAllCVs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force refresh

  // Listen for CV upload/analysis completion events
  useEffect(() => {
    const handleCVUpdate = () => {
      console.log('CompaniesMatchingWithLayout - CV update detected, refreshing...');
      setRefreshKey(prev => prev + 1); // Trigger refresh
    };

    // Listen for custom events when CV is uploaded or analyzed
    window.addEventListener('cv-uploaded', handleCVUpdate);
    window.addEventListener('cv-analyzed', handleCVUpdate);
    window.addEventListener('cv-analysis-complete', handleCVUpdate);

    return () => {
      window.removeEventListener('cv-uploaded', handleCVUpdate);
      window.removeEventListener('cv-analyzed', handleCVUpdate);
      window.removeEventListener('cv-analysis-complete', handleCVUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchLatestCVAnalysis = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use the existing listCVs service
        const response = await listCVs();

        if (response.success && response.data) {
          let cvs = response.data;
          
          // Handle paginated response
          if (cvs && typeof cvs === 'object' && !Array.isArray(cvs) && (cvs as any).results) {
            cvs = (cvs as any).results;
          }
          
          // Ensure cvs is an array
          if (!Array.isArray(cvs)) {
            console.error('JobMatchingWithLayout - CV data is not an array:', cvs);
            setError('Invalid CV data format received from server');
            return;
          }

          // Store all CVs for selection
          setAllCVs(cvs);
          
          if (cvs.length > 0) {
            // Get the latest CV analysis
            const latestCV = cvs[0] as any; // CVs are ordered by -uploaded_at, so first is latest
            
            // Check if this is a different CV than what we currently have
            const currentLatestCVId = cvs.length > 0 ? cvs[0]?.id : null;
            const hasNewCV = currentLatestCVId && (!cvAnalysisId || cvs[0]?.analysis?.id !== cvAnalysisId);

            // Check if the CV already has an analysis
            if (latestCV.analysis && latestCV.analysis.id) {
              // Only update if this is a new CV or different analysis
              if (hasNewCV || latestCV.analysis.id !== cvAnalysisId) {
                console.log('CompaniesMatchingWithLayout - New CV analysis detected:', latestCV.analysis.id);
                setCvAnalysisId(latestCV.analysis.id);
                
                // Extract analysis data from the existing analysis
                const comprehensiveAnalysisData = {
                  detected_industry: latestCV.analysis.detected_industry,
                  detected_language: latestCV.analysis.detected_language,
                  cv_keywords: latestCV.analysis.cv_keywords || [],
                  technical_skills: latestCV.analysis.technical_skills || [],
                  suggested_profiles: latestCV.analysis.suggested_profiles || [],
                  experience_level: latestCV.analysis.market_analysis?.experience_level,
                  experience_years: latestCV.analysis.market_analysis?.experience_years,
                  overall_score: latestCV.analysis.overall_score,
                  keyword_match_percentage: latestCV.analysis.keyword_match_percentage,
                  full_analysis_data: latestCV.analysis.analysis_data,
                  market_analysis: latestCV.analysis.market_analysis
                };

                setAnalysisData(comprehensiveAnalysisData);
              }
            } else {

              // Use proxy (Vite in dev, Vercel in production) to bypass CORS
              const apiBaseUrl = '/api';
              const analysisResponse = await fetch(`${apiBaseUrl}/ats/cvs/${latestCV.id}/analyze/`, {
                method: 'POST',
                headers: {
                  'Authorization': `Token ${localStorage.getItem('authToken') || sessionStorage.getItem('authToken')}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
              });

              if (analysisResponse.ok) {
                const analysisResult = await analysisResponse.json();

                if (analysisResult.analysis && analysisResult.analysis.id) {
                  setCvAnalysisId(analysisResult.analysis.id);
                  
                  // Extract comprehensive analysis data for JobMatching component
                  const comprehensiveAnalysisData = {
                    detected_industry: analysisResult.analysis?.detected_industry,
                    detected_language: analysisResult.analysis?.detected_language,
                    cv_keywords: analysisResult.analysis?.industry_keywords || [],
                    technical_skills: analysisResult.analysis?.categorized_keywords?.technical_skills || [],
                    suggested_profiles: analysisResult.analysis?.suggested_profiles || [],
                    experience_level: analysisResult.analysis?.market_analysis?.experience_level,
                    experience_years: analysisResult.analysis?.market_analysis?.experience_years,
                    overall_score: analysisResult.analysis?.overall_score,
                    keyword_match_percentage: analysisResult.analysis?.keyword_match_percentage,
                    full_analysis_data: analysisResult.analysis?.analysis_data,
                    market_analysis: analysisResult.analysis?.market_analysis
                  };

                  setAnalysisData(comprehensiveAnalysisData);
                } else {
                  console.error('JobMatchingWithLayout - No analysis ID in response:', analysisResult);
                  setError('Failed to get analysis ID from CV analysis');
                }
              } else {
                const errorText = await analysisResponse.text();
                console.error('JobMatchingWithLayout - Analysis failed:', analysisResponse.status, errorText);
                setError(`CV analysis failed: ${analysisResponse.status}`);
              }
            }
          } else {

            setError('No CVs found. Please upload a CV first.');
          }
        } else {
          console.error('JobMatchingWithLayout - Failed to fetch CVs:', response.errors);
          setError(response.errors?.general || 'Failed to fetch CVs');
        }
      } catch (error) {
        console.error('JobMatchingWithLayout - Error fetching CV analysis:', error);
        setError(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestCVAnalysis();
  }, [refreshKey]); // Re-run when refreshKey changes

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading...</span>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="w-full h-full px-2 py-8 relative" style={{ height: '100%' }}>
          {/* Main Content */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden relative z-10 flex flex-col" style={{ flex: 1, height: 'calc(100vh - 10rem)', maxHeight: 'calc(100vh - 10rem)' }}>
            
            {/* Premium Header */}
            <div className="bg-gradient-to-r from-slate-900 via-orange-900 to-purple-900 text-white px-6 py-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-xl">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">AI Company Matching</h2>
                    <p className="text-white/80 text-sm">Find companies that match your profile</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                    0 Companies
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              <div className="p-6 space-y-6">
                
                {/* Empty State - No CVs */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-4xl mx-auto">
                    {/* Hero Section - Compact */}
                    <div className="mb-6">
                      <div className="relative mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-purple-600 rounded-2xl mx-auto flex items-center justify-center shadow-xl">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center animate-bounce">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        Find Your Perfect Companies
                      </h2>
                      <p className="text-gray-600 mb-4">
                        Discover companies that match your skills and industry with AI-powered matching.
                      </p>
                    </div>

                    {/* Features Grid - Compact */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-4 border border-blue-200 hover:shadow-md transition-all duration-300">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1 text-sm">Smart Matching</h3>
                        <p className="text-xs text-gray-600">AI-powered company discovery</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-4 border border-green-200 hover:shadow-md transition-all duration-300">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1 text-sm">Perfect Fit</h3>
                        <p className="text-xs text-gray-600">Companies that match your profile</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl p-4 border border-purple-200 hover:shadow-md transition-all duration-300">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1 text-sm">Career Growth</h3>
                        <p className="text-xs text-gray-600">Opportunities for advancement</p>
                      </div>
                    </div>

                    {/* Call to Action - Compact */}
                    <div className="bg-gradient-to-r from-orange-50 to-purple-50 rounded-xl p-6 border border-orange-200">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Ready to Find Companies?
                      </h3>
                      <p className="text-gray-600 mb-4 text-sm">
                        Upload your resume to discover companies that match your skills and career goals.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/cv-upload')}
                          className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span>Upload Resume</span>
                        </button>
                        <button
                          onClick={() => navigate('/dashboard')}
                          className="bg-white border-2 border-orange-200 text-orange-600 hover:bg-orange-50 px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          <span>Back to Dashboard</span>
            </button>
                      </div>
                    </div>

                    {/* Stats Section - Compact */}
                    <div className="mt-6 grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-xl font-bold text-orange-600 mb-1">500+</div>
                        <div className="text-xs text-gray-600">Companies</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-purple-600 mb-1">95%</div>
                        <div className="text-xs text-gray-600">Match Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-green-600 mb-1">24h</div>
                        <div className="text-xs text-gray-600">Response Time</div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <CompaniesMatching 
        onComplete={() => navigate('/interview-prep')} 
        onStartOver={() => navigate('/cv-upload')} 
        onBack={() => navigate('/cv-upload')} 
        onGoToInterviewPrep={() => navigate('/interview-prep')} 
        cvAnalysisId={cvAnalysisId || undefined}
        analysisData={analysisData}
        allCVs={allCVs}
      />
    </AppLayout>
  );
}

// Interview Prep with AppLayout
function InterviewPrepWithLayout() {
  const navigate = useNavigate();
  const [interviewView, setInterviewView] = useState<'new' | 'list' | 'view'>('new');
  const [selectedPrepId, setSelectedPrepId] = useState<string | null>(null);

  const handleViewPreparations = () => {
    setInterviewView('list');
  };

  const handleViewPrep = (prepId: string) => {
    setSelectedPrepId(prepId);
    setInterviewView('view');
  };

  const handleCreateNew = () => {
    setInterviewView('new');
  };

  const handleBackToList = () => {
    setInterviewView('list');
  };

  const handleBackToNew = () => {
    setInterviewView('new');
  };

  const handleDeletePrep = (_prepId: string) => {
    // After deletion, go back to list
    setInterviewView('list');
  };

  const renderInterviewComponent = () => {
    switch (interviewView) {
      case 'list':
        return (
          <InterviewPrepList
            onViewPrep={handleViewPrep}
            onCreateNew={handleCreateNew}
            onBack={handleBackToNew}
          />
        );
      case 'view':
        return selectedPrepId ? (
          <InterviewPrepView
            prepId={selectedPrepId}
            onBack={handleBackToList}
            onEdit={(_prepId) => {
              // For now, just go back to list. Could implement edit functionality later
              setInterviewView('list');
            }}
            onDelete={handleDeletePrep}
          />
        ) : null;
      default:
        return (
          <InterviewPrepNew 
            onComplete={() => navigate('/profile-boost')} 
            onStartOver={() => navigate('/cv-upload')} 
            onBack={() => navigate('/company-matching')}
            onViewPreparations={handleViewPreparations}
          />
        );
    }
  };

  return (
    <AppLayout>
      {renderInterviewComponent()}
    </AppLayout>
  );
}

// Dashboard with AppLayout
function DashboardWithLayout() {
  return (
    <AppLayout>
      <Dashboard />
    </AppLayout>
  );
}

// Responsive Dashboard Layout
function ResponsiveDashboardWithLayout() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileDashboard />
      </MobileLayout>
    );
  }

  return (
    <AppLayout>
      <Dashboard />
    </AppLayout>
  );
}

// Mobile Dashboard with MobileLayout
function MobileDashboardWithLayout() {
  return (
    <MobileLayout>
      <MobileDashboard />
    </MobileLayout>
  );
}

function App() {
  // Initialize console filter to reduce noise from third-party storage warnings
  useEffect(() => {
    initConsoleFilter();
  }, []);
  
  // Initialize performance monitoring
  usePerformance();
  
  // Initialize performance optimizations
  useEffect(() => {
    initPerformanceOptimizations();
  }, []);

  return (
    <HelmetProvider>
      <CookieProvider>
        <AuthProvider>
          <Router>
            <ErrorBoundary>
              <AnalyticsProvider trackingId={import.meta.env.VITE_GA_TRACKING_ID}>
                <ScrollToTop />
                <LanguageHandler />
                <AccessibilityEnhancer />
                <SecurityEnhancer />
                <MobileOptimizer />
                <Suspense fallback={<LoadingSpinner size="lg" text="Loading MotivaMatch..." />}>
                  <Routes>
            <Route path="/" element={<LandingPageWithNav />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/mission" element={<OurMission />} />
            <Route path="/team" element={<Team />} />
            <Route path="/products" element={<Products />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<Article />} />
            <Route path="/career-acceleration" element={<CareerAcceleration />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/article-kpis" element={<SuperUserRoute><ArticleKPIs /></SuperUserRoute>} />
            <Route path="/jobs-in-:country" element={<JobsInCountry />} />
            <Route path="/job-search" element={<JobSearch />} />
            <Route path="/company-in-:country" element={<CompaniesInCountry />} />
            <Route path="/cv-scanner" element={<CVScanner />} />
            <Route path="/profile-boost" element={<ProfileBoost />} />
            <Route path="/company-match" element={<CompanyMatch />} />
            <Route path="/interview-prep" element={<InterviewPrep />} />
            <Route path="/ats-guide" element={<ATSGuide />} />
            <Route path="/cv-tips" element={<CVTips />} />
            <Route path="/support" element={<Support />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/gdpr" element={<GDPR />} />
            <Route path="/upload-cv" element={<PreAuthCVUpload />} />
            <Route path="/auth" element={<AuthWithNav />} />
            <Route path="/reset-password" element={<AuthWithNav />} />
            {/* Module 1: CV Scanner - Free for everyone */}
            <Route path="/cv-upload" element={<ProtectedRoute><CVUploadWithLayout /></ProtectedRoute>} />
            
            {/* Module 2: Profile Boost - Requires Basic or Premium */}
            <Route 
              path="/profile-boost-functional" 
              element={
                <PremiumRoute 
                  moduleId="profile-boost" 
                  moduleName="Profile Boost"
                >
                  <ProfileBoostFunctionalWithLayout />
                </PremiumRoute>
              } 
            />
            
            {/* Module 3: Company Matching - Requires Basic or Premium */}
            <Route 
              path="/company-matching" 
              element={
                <PremiumRoute 
                  moduleId="company-match" 
                  moduleName="Company Matching"
                >
                  <CompaniesMatchingWithLayout />
                </PremiumRoute>
              } 
            />
            
            {/* Module 4: Interview Prep - Requires Premium only */}
            <Route 
              path="/interview-prep-functional" 
              element={
                <PremiumRoute 
                  moduleId="interview-prep" 
                  moduleName="Interview Preparation"
                >
                  <InterviewPrepWithLayout />
                </PremiumRoute>
              } 
            />
            <Route path="/dashboard" element={<ProtectedRoute><ResponsiveDashboardWithLayout /></ProtectedRoute>} />
            <Route path="/dashboard-page" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/mobile-dashboard" element={<ProtectedRoute><MobileDashboardWithLayout /></ProtectedRoute>} />
            <Route path="/applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
            <Route path="/openai-job-search" element={<ProtectedRoute><OpenAIJobSearch /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/success" element={<PaymentSuccess />} />
            <Route path="/cancel" element={<PaymentCancel />} />
            <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AnalyticsProvider>
              <CookieConsent />
              <AdBlockNotice />
              {/* Load AdSense script (only in production, with safety checks) */}
              <AdSenseLoader />
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </CookieProvider>
    </HelmetProvider>
  );
}

export default App;

